import { useEffect, useState } from 'react';
import chatblkIcon from '../../../../../../assets/icons/chatblk.svg?raw';
import chatblkLogo from '../../../../../../assets/icons/chatblk-beta.svg?raw';
import chatblkSparkle from '../../../../../../assets/icons/sparkle.svg?raw';
import { joinString } from '../../../../../../_helpers/join-string';
import { ChatBlkButton } from '../../../../../../components/blackrock/advisor-center/_types/header';
import ChatblkTokenIframe from './ChatblkTokenIframe';
import { chatblkConfig } from './ChatblkConfigUtility';

import './ChatBlkIcon.scss';

type ChatBlkEventDetail = {
	action: 'close' | 'open';
	summarize?: boolean;
	relatedQuestion?: string;
};

export const CHATBLK_TOGGLE_EVENT = 'chatblktoggle';

export function ChatBLKIcon({
	dataLinkEvent,
	label,
	dataLinkEventScroll,
	labelScroll,
}: ChatBlkButton) {
	const [open, setOpen] = useState(false);
	const [showSideButton, setShowSideButton] = useState(false);
	const [chatblkIframeEnabled, setChatblkIframeEnabled] = useState(false);
	const [isFirstLoad, setIsFirstLoad] = useState(true);
	const [shouldHideButton, setShouldHideButton] = useState(false); // Track if the button should hide after animation

	// Initialize configuration and fetch the login page URL
	useEffect(() => {
		chatblkConfig.getDomainConfig();
		chatblkConfig.getLoginPageUrl();

		const iframeEnabled = localStorage.getItem('chatblk_iframe_enabled');
		setChatblkIframeEnabled(iframeEnabled === 'true');
	}, []);

	// Get the login page URL from the configuration utility
	const iframeSrc = chatblkConfig.getLoginPageUrl();

	// Handle animation timeout
	useEffect(() => {
		const animationTimeout = setTimeout(() => {
			setIsFirstLoad(false); // Mark animation as complete

			// If the button should be hidden after the animation, hide it
			if (shouldHideButton) {
				setShowSideButton(false);
			} else {
				setShowSideButton(true); // Otherwise, show the button
			}
		}, 2400);

		return () => clearTimeout(animationTimeout);
	}, [shouldHideButton]);

	// Handle sidebar class
	useEffect(() => {
		const sidebar = document.querySelector('.dashboard-navigation-sidebar');
		if (sidebar) {
			document.body.classList.add('blk-ac-has-dashboard-sidebar');
		} else {
			document.body.classList.remove('blk-ac-has-dashboard-sidebar');
		}
	}, []);

	// Handle main button click
	function onClick() {
		window.dispatchEvent(
			new CustomEvent<ChatBlkEventDetail>(CHATBLK_TOGGLE_EVENT, {
				detail: { action: open ? 'close' : 'open' },
			})
		);
	}

	// Handle side button click
	function onSideButtonClick() {
		if (isFirstLoad) {
			// If the animation is still active, mark the button to hide after animation
			setShouldHideButton(true);
		} else {
			// Hide the button immediately if the animation is complete
			setShowSideButton(false);
		}

		// Dispatch the event to open ChatBlk
		window.dispatchEvent(
			new CustomEvent<ChatBlkEventDetail>(CHATBLK_TOGGLE_EVENT, {
				detail: { action: 'open' },
			})
		);
	}

	// Listen for toggle events
	useEffect(() => {
		function toggleEvent(e: Event) {
			if (isChatBlkToggleEventWithDetail(e)) {
				const isOpening = e.detail.action === 'open';
				setOpen(isOpening);

				if (isOpening) {
					// Always hide side button when opening
					setShowSideButton(false);
				} else {
					setShowSideButton(true);
				}
			}
		}

		window.addEventListener(CHATBLK_TOGGLE_EVENT, toggleEvent);

		return () => {
			window.removeEventListener(CHATBLK_TOGGLE_EVENT, toggleEvent);
		};
	}, []);

	return (
		<>
			<button
				data-link-event={dataLinkEvent}
				aria-label={label}
				onClick={onClick}
				aria-pressed={open}
				className={joinString([
					'blk-ac-chatblk-icon-container',
					'blk-ac-chatblk-hidden-button',
					open ? 'open' : '',
				])}
				dangerouslySetInnerHTML={{ __html: chatblkIcon }}
			></button>

			<div
				className={joinString([
					'blk-ac-side-chatblk-container',
					showSideButton ? 'show' : '',
					isFirstLoad ? 'animate-on-load' : '',
				])}
			>
				<button
					data-link-event={dataLinkEventScroll}
					aria-label={labelScroll}
					aria-pressed={open}
					onClick={onSideButtonClick}
					className="blk-ac-chatblk-button"
				>
					<span
						className="blk-ac-chatblk-icon"
						dangerouslySetInnerHTML={{ __html: chatblkSparkle }}
					/>
					<span
						className="blk-ac-chatblk-logo"
						dangerouslySetInnerHTML={{ __html: chatblkLogo }}
					/>
					<span className="blk-ac-chatblk-tm">TM</span>
				</button>
			</div>
			{chatblkIframeEnabled && <ChatblkTokenIframe iframeSrc={iframeSrc} />}
		</>
	);
}

function isChatBlkToggleEventWithDetail(
	e: Event
): e is CustomEvent<ChatBlkEventDetail> {
	return (
		typeof (e as CustomEvent).detail?.action === 'string' &&
		['close', 'open'].includes((e as CustomEvent).detail.action)
	);
}
